import { API_BASE_URL } from '../api';
import { authService } from './authService';

export interface BookingResponse {
  id: number;
  service_id: number;
  start_time: string;
  end_time: string;
  status: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address_line1?: string;
  customer_address_line2?: string;
  customer_postal_code?: string;
  customer_city?: string;
  customer_country?: string;
  notes?: string;
  created_at: string;
  payment_status?: string;
  amount_paid_cents?: number;
  gift_voucher_id?: number;
  gift_voucher_number?: string;
  practitioners: Array<{
    id: number;
    name: string;
  }>;
  room?: {
    id: number;
    name: string;
    type?: string;
  };
  service?: {
    id: number;
    name: string;
    duration_minutes: number;
    price?: number;
    buffer_before_minutes?: number;
    buffer_after_minutes?: number;
    room_type_required?: string | null;
  };
}

export interface DashboardStats {
  today_bookings: number;
  week_bookings: number;
  month_bookings: number;
  canceled_bookings: number;
  valid_vouchers: number;
  used_vouchers_month: number;
  expired_vouchers_month: number;
  upcoming_bookings: Array<{
    id: number;
    customer_name: string;
    service_name: string;
    start_time: string;
    date: string;
    time: string;
  }>;
  practitioner_name: string;
}

class BackofficeService {
    async getBookings(practitionerIds?: number[], startDate?: string, endDate?: string, includeAll?: boolean): Promise<BookingResponse[]> {
    const headers = await authService.getAuthHeaders();
    
        const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (practitionerIds && practitionerIds.length > 0) {
      practitionerIds.forEach(id => params.append('practitioner_ids', id.toString()));
    }
    if (includeAll) {
      params.append('include_all', 'true');
    }
    
    const url = `${API_BASE_URL}/api/backoffice/bookings${params.toString() ? `?${params.toString()}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch bookings');
    }

    return response.json();
  }

  async getBookingDetail(bookingId: number): Promise<BookingResponse> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/bookings/${bookingId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch booking details');
    }

    return response.json();
  }

  async updateBookingNotes(bookingId: number, notes: string): Promise<void> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/bookings/${bookingId}/notes`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ notes }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update booking notes');
    }
  }

  async cancelBooking(bookingId: number, reason?: string): Promise<void> {
    const headers = await authService.getAuthHeaders();
    
    const body: any = {};
    if (reason) body.reason = reason;
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/bookings/${bookingId}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to cancel booking');
    }
  }

  async createAdminBooking(payload: {
    service_id: number;
    start_time: string;
    customer_name: string;
    customer_email: string;
    customer_phone?: string;
    customer_address_line1?: string;
    customer_address_line2?: string;
    customer_postal_code?: string;
    customer_city?: string;
    customer_country?: string;
    notes?: string;
    admin_notes?: string;
  }): Promise<BookingResponse> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/bookings`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create admin booking');
    }

    return response.json();
  }

  async getDashboardStats(): Promise<DashboardStats> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/dashboard/stats`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch dashboard stats');
    }

    return response.json();
  }

  async getPractitioners(): Promise<Array<{ id: number; name: string }>> {
    const headers = await authService.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/backoffice/practitioners`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch practitioners');
    }

    return response.json();
  }

  async createDailyAvailability(payload: any): Promise<any> {
    // Crée des disponibilités ponctuelles pour une journée
    // Format: {practitioner_id, date, time_slots}
    
    const headers = await authService.getAuthHeaders();
    
    // Le payload peut être soit le nouveau format direct, soit l'ancien format à convertir
    let dailyPayload;
    
    if (payload.date) {
      // Nouveau format direct
      dailyPayload = {
        practitioner_id: payload.practitioner_id,
        date: payload.date,
        time_slots: payload.time_slots.map((slot: any) => ({
          start: slot.start_time || slot.start,
          end: slot.end_time || slot.end
        }))
      };
    } else {
      // Ancien format (pour compatibilité)
      const targetDate = payload.start_date || new Date().toISOString().split('T')[0];
      dailyPayload = {
        practitioner_id: payload.practitioner_id,
        date: targetDate,
        time_slots: payload.time_slots.map((slot: any) => ({
          start: slot.start_time,
          end: slot.end_time
        }))
      };
    }

    const response = await fetch(`${API_BASE_URL}/api/availability/daily`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dailyPayload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create daily availability');
    }

    const result = await response.json();
    
    // Adapter la réponse pour maintenir la compatibilité
    return {
      created_count: result.length,
      availabilities: result,
      message: `Créé ${result.length} créneaux de disponibilité pour le ${dailyPayload.date}`
    };
  }

  // Méthode de compatibilité (deprecated)
  async createWeeklyAvailability(payload: any): Promise<any> {
    console.warn('createWeeklyAvailability is deprecated, use createDailyAvailability instead');
    return this.createDailyAvailability(payload);
  }

  async getAvailabilities(practitionerId?: number, startDate?: string, endDate?: string): Promise<any[]> {
    const headers = await authService.getAuthHeaders();
    const params = new URLSearchParams();
    if (practitionerId) params.append('practitioner_id', practitionerId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await fetch(`${API_BASE_URL}/api/backoffice/availabilities?${params.toString()}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch availabilities');
    }

    return response.json();
  }

  async deleteAvailability(availabilityId: number): Promise<void> {
    const headers = await authService.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/backoffice/availabilities/${availabilityId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok && response.status !== 204) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete availability');
    }
  }
  async getRooms(): Promise<Array<{ id: number; name: string; room_type: string }>> {
    const response = await fetch(`${API_BASE_URL}/api/rooms`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch rooms');
    }

    return response.json();
  }

  async getRoomClosures(roomId?: number): Promise<Array<{
    id: number;
    room_id: number;
    room_name: string;
    start_datetime: string;
    end_datetime: string;
    reason: string | null;
  }>> {
    const headers = await authService.getAuthHeaders();
    const params = roomId ? `?room_id=${roomId}` : '';
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/room-closures${params}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch room closures');
    }

    return response.json();
  }

  async createRoomClosure(data: {
    room_id: number;
    start_datetime: string;
    end_datetime: string;
    reason: string | null;
  }): Promise<void> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/room-closures`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create room closure');
    }
  }

  async deleteRoomClosure(closureId: number): Promise<void> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/room-closures/${closureId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to delete room closure');
    }
  }

  async getSystemSettings(): Promise<{
    notification_email: string;
    smtp_host: string | null;
    smtp_port: number;
    smtp_user: string | null;
    email_from: string;
  }> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/system-settings`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch system settings');
    }

    return response.json();
  }

  async updateSystemSettings(data: {
    notification_email: string;
  }): Promise<void> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/system-settings`, {
      method: 'PUT',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update system settings');
    }
  }

  async getConstructionBanner(): Promise<{
    enabled: boolean;
    text: string;
  }> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/system-settings/construction-banner`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch construction banner config');
    }

    return response.json();
  }

  async updateConstructionBanner(data: {
    enabled: boolean;
    text: string;
  }): Promise<void> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/system-settings/construction-banner`, {
      method: 'PUT',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update construction banner');
    }
  }

  async getInfoBanner(): Promise<{
    enabled: boolean;
    text: string;
  }> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/system-settings/info-banner`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch info banner config');
    }

    return response.json();
  }

  async updateInfoBanner(data: {
    enabled: boolean;
    text: string;
  }): Promise<void> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/system-settings/info-banner`, {
      method: 'PUT',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update info banner');
    }
  }

  async getWelcomePopup(): Promise<{
    enabled: boolean;
    image_url: string;
    text: string;
  }> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/system-settings/welcome-popup`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch welcome popup config');
    }

    return response.json();
  }

  async updateWelcomePopup(data: {
    enabled: boolean;
    image_url: string;
    text: string;
  }): Promise<void> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/system-settings/welcome-popup`, {
      method: 'PUT',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update welcome popup');
    }
  }

  async uploadWelcomePopupImage(file: File): Promise<string> {
    const headers = await authService.getAuthHeaders();
    // Remove Content-Type so the browser sets multipart/form-data with boundary
    const { 'Content-Type': _, ...uploadHeaders } = headers as Record<string, string>;
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/system-settings/welcome-popup/upload-image`, {
      method: 'POST',
      headers: uploadHeaders,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload image');
    }

    const data = await response.json();
    return data.image_url;
  }

  async getUnreadNotificationsCount(): Promise<number> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/api/backoffice/notifications/unread-count`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch unread notifications count');
    }

    const data = await response.json();
    return data.count || 0;
  }

  async getAvailablePractitionersForBooking(bookingId: number): Promise<Array<{ id: number; name: string }>> {
    const headers = await authService.getAuthHeaders();
    
    const url = `${API_BASE_URL}/api/bookings/${bookingId}/available-practitioners`;
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const text = await response.text();
      console.error('Failed to fetch practitioners:', { status: response.status, url, text });
      throw new Error(`Failed to fetch available practitioners (${response.status}): ${text.substring(0, 200)}`);
    }

    return response.json();
  }

  async getRescheduleSlots(bookingId: number, date: string): Promise<Array<{ start_time: string; end_time: string }>> {
    const headers = await authService.getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}/api/backoffice/bookings/${bookingId}/reschedule-slots?date=${date}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch reschedule slots');
    }

    return response.json();
  }

  async rescheduleBooking(bookingId: number, newStartTime: string, notifyCustomer: boolean = true): Promise<BookingResponse> {
    const headers = await authService.getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/backoffice/bookings/${bookingId}/reschedule`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        new_start_time: newStartTime,
        notify_customer: notifyCustomer,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to reschedule booking');
    }

    return response.json();
  }

  async changePractitioner(bookingId: number, newPractitionerId: number): Promise<void> {
    const headers = await authService.getAuthHeaders();
    
    const response = await fetch(
      `${API_BASE_URL}/api/bookings/${bookingId}/change-practitioner?new_practitioner_id=${newPractitionerId}`,
      {
        method: 'PUT',
        headers,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to change practitioner');
    }
  }
}

export const backofficeService = new BackofficeService();
